import styled from 'styled-components';

import {
  MENU_ROW,
  MENU_SEPARATOR,
  MENU_KEY,
  MENU_SHORTCUT,
  MENU_SURFACE,
} from '../../styles/menu';

/**
 * Best-effort initial clamp before a menu measures itself. Assumes a
 * ~140px×120px menu so the first paint already lands inside the viewport
 * even though the precise size depends on font rendering.
 */
export function initialClamp(x: number, y: number): { x: number; y: number } {
  const margin = 8;
  const approxW = 140;
  const approxH = 120;
  if (typeof window === 'undefined') return { x, y };
  return {
    x: Math.max(margin, Math.min(x, window.innerWidth - approxW - margin)),
    y: Math.max(margin, Math.min(y, window.innerHeight - approxH - margin)),
  };
}

/**
 * The right-click / popover menu panel.
 *
 * Everything visual comes from the shared schema in "styles/menu"; what stays
 * here is only what is specific to a menu positioned at a pointer: fixed
 * placement, a very high stacking order, and a floor width so a two-item menu
 * is not a sliver. No transition — the menu appears at its measured position,
 * and "useLayoutEffect" guarantees the first paint is already clamped.
 */
export const MenuPositioned = styled.div`
  ${MENU_SURFACE}
  position: fixed;
  z-index: 99999;
  min-width: 140px;
`;

export const MenuItem = styled.button<{ $danger?: boolean }>`
  ${MENU_ROW}
  /* "$danger" is the one deviation a row may make from the schema, and it is
     reserved for irreversible actions — spending the error colour on anything
     routine makes it mean less where it matters. */
  color: ${(p) =>
    p.$danger === true ? 'var(--error-color, #e3415a)' : 'var(--text-primary)'};

  &:hover:not(:disabled) {
    color: ${(p) =>
      p.$danger === true
        ? 'var(--error-color, #e3415a)'
        : 'var(--text-primary)'};
  }
`;

/**
 * Hairline between menu groups. Groups exist so a destructive action never
 * sits flush against a routine one — "Log Out" being one careless pixel from
 * "Settings" is how people log themselves out by accident.
 */
export const MenuSeparator = styled.div`
  ${MENU_SEPARATOR}
`;

/**
 * Right-aligned keyboard hint inside a menu item. "margin-left: auto" rather
 * than a fixed column so it stays flush right whatever the label length, and
 * the item keeps its natural width.
 */
export const MenuShortcut = styled.span`
  ${MENU_SHORTCUT}
`;

/** One key cap inside a `MenuShortcut`. */
export const MenuKey = styled.kbd`
  ${MENU_KEY}
`;
